import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '../config';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});
const driverIcon = new L.Icon({ iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png', shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png', iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41] });
const originIcon = new L.Icon({ iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png', shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png', iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41] });
const destIcon = new L.Icon({ iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png', shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png', iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41] });

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length >= 2) {
      map.fitBounds(points.map(p => [p.lat, p.lng]), { padding: [50, 50] });
    } else if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 14);
    }
  }, [points, map]);
  return null;
}

const STATUSES = { requested: 'Solicitado', accepted: 'Conductor asignado', driver_arriving: 'Conductor en camino', in_progress: 'En viaje', completed: 'Completado', cancelled: 'Cancelado' };

const TransportComponent = ({ user }) => {
  const [tab, setTab] = useState('passenger');
  const [myLoc, setMyLoc] = useState({ lat: 10.4806, lng: -66.9036 });
  const [destLoc, setDestLoc] = useState(null);
  const [destName, setDestName] = useState('');
  const [originName, setOriginName] = useState('Mi ubicacion');
  const [activeRide, setActiveRide] = useState(null);
  const [driverLoc, setDriverLoc] = useState(null);
  const [availableRides, setAvailableRides] = useState([]);
  const [myRides, setMyRides] = useState([]);
  const [driverProfile, setDriverProfile] = useState(null);
  const [nearbyDrivers, setNearbyDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regData, setRegData] = useState({ vehicle_type: 'auto', vehicle_description: '', license_plate: '' });
  const [ratingValue, setRatingValue] = useState(5);
  const [predefinedRoutes, setPredefinedRoutes] = useState({});
  const [routeEta, setRouteEta] = useState(null);
  const [selectedCity, setSelectedCity] = useState('');
  const wsRef = useRef(null);

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      (pos) => setMyLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {}, { enableHighAccuracy: true, timeout: 10000 }
    );
    fetchData();
    fetchRoutes();

    // WebSocket for live tracking
    const token = localStorage.getItem('token');
    if (token) {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${new URL(BACKEND_URL).host}/ws/chat/${token}`;
      const ws = new WebSocket(wsUrl);
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'driver_location') setDriverLoc(data.location);
          if (data.type === 'ride_accepted') fetchData();
          if (data.type === 'ride_started') fetchData();
          if (data.type === 'ride_completed') { fetchData(); setDriverLoc(null); }
          if (data.type === 'ride_request') fetchData();
        } catch (err) {}
      };
      wsRef.current = ws;
    }
    return () => wsRef.current?.close();
  }, []);

  const fetchData = async () => {
    try {
      const [ridesRes, driverRes] = await Promise.all([
        axios.get(`${API}/transport/rides/my`),
        axios.get(`${API}/transport/driver/me`).catch(() => ({ data: { driver: null } }))
      ]);
      setMyRides(ridesRes.data.rides || []);
      setDriverProfile(driverRes.data.driver);

      const active = ridesRes.data.rides?.find(r => !['completed', 'cancelled'].includes(r.status));
      setActiveRide(active || null);
      if (active?.driver_location) setDriverLoc(active.driver_location);

      if (driverRes.data.driver) {
        const avRes = await axios.get(`${API}/transport/rides/available`);
        setAvailableRides(avRes.data.rides || []);
      }
    } catch (err) {}
    finally { setLoading(false); }
  };

  const fetchRoutes = async () => {
    try {
      const res = await axios.get(`${API}/transport/routes/predefined`);
      setPredefinedRoutes(res.data.routes || {});
    } catch (err) { console.error(err); }
  };

  const selectRoute = async (route) => {
    setDestLoc({ lat: route.lat, lng: route.lng });
    setDestName(route.name);
    // Calculate ETA
    try {
      const res = await axios.get(`${API}/transport/routes/eta`, {
        params: { origin_lat: myLoc.lat, origin_lng: myLoc.lng, dest_lat: route.lat, dest_lng: route.lng }
      });
      setRouteEta(res.data);
    } catch (err) { console.error(err); }
  };

  const calculateEtaForDest = async (lat, lng) => {
    try {
      const res = await axios.get(`${API}/transport/routes/eta`, {
        params: { origin_lat: myLoc.lat, origin_lng: myLoc.lng, dest_lat: lat, dest_lng: lng }
      });
      setRouteEta(res.data);
    } catch (err) { console.error(err); }
  };

  const requestRide = async () => {
    if (!destLoc) { alert('Selecciona un destino en el mapa o una ruta predefinida'); return; }
    try {
      const res = await axios.post(`${API}/transport/ride/request`, {
        origin_lat: myLoc.lat, origin_lng: myLoc.lng,
        dest_lat: destLoc.lat, dest_lng: destLoc.lng,
        origin_name: originName, dest_name: destName || 'Destino'
      });
      setActiveRide(res.data.ride);
      setRouteEta(null);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const acceptRide = async (rideId) => {
    try {
      await axios.post(`${API}/transport/ride/${rideId}/accept`);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const startRide = async () => {
    try { await axios.post(`${API}/transport/ride/${activeRide.id}/start`); fetchData(); }
    catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const completeRide = async () => {
    try { await axios.post(`${API}/transport/ride/${activeRide.id}/complete`); setDriverLoc(null); fetchData(); }
    catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const cancelRide = async () => {
    if (!activeRide) return;
    try { await axios.post(`${API}/transport/ride/${activeRide.id}/cancel`); setActiveRide(null); setDriverLoc(null); fetchData(); }
    catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const rateRide = async (rideId) => {
    try { await axios.post(`${API}/transport/ride/${rideId}/rate`, { rating: ratingValue }); fetchData(); }
    catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const registerDriver = async () => {
    try { await axios.post(`${API}/transport/driver/register`, regData); fetchData(); }
    catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const toggleDriverStatus = async () => {
    const newStatus = driverProfile?.status === 'available' ? 'offline' : 'available';
    try {
      await axios.post(`${API}/transport/driver/status`, { status: newStatus, latitude: myLoc.lat, longitude: myLoc.lng });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const mapPoints = [];
  if (activeRide) {
    mapPoints.push({ lat: activeRide.origin.lat, lng: activeRide.origin.lng });
    mapPoints.push({ lat: activeRide.destination.lat, lng: activeRide.destination.lng });
  } else {
    mapPoints.push(myLoc);
    if (destLoc) mapPoints.push(destLoc);
  }

  if (loading) return <div className="p-8 text-center text-gray-400">Cargando...</div>;

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="transport-module">
      {/* Tabs */}
      <div className="flex gap-2">
        <button onClick={() => setTab('passenger')} data-testid="tab-passenger"
          className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'passenger' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>
          Pasajero
        </button>
        <button onClick={() => setTab('driver')} data-testid="tab-driver"
          className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'driver' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>
          Conductor
        </button>
        <button onClick={() => setTab('history')} data-testid="tab-history"
          className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'history' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>
          Historial
        </button>
      </div>

      {/* Map */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ height: '350px' }} data-testid="transport-map">
        <MapContainer center={[myLoc.lat, myLoc.lng]} zoom={13} style={{ height: '100%', width: '100%' }}
          onClick={(e) => { if (!activeRide && tab === 'passenger') { setDestLoc({ lat: e.latlng.lat, lng: e.latlng.lng }); calculateEtaForDest(e.latlng.lat, e.latlng.lng); } }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
          <FitBounds points={mapPoints} />

          {/* Origin */}
          <Marker position={[activeRide ? activeRide.origin.lat : myLoc.lat, activeRide ? activeRide.origin.lng : myLoc.lng]} icon={originIcon}>
            <Popup>{activeRide ? activeRide.origin.name : 'Tu ubicacion'}</Popup>
          </Marker>

          {/* Destination */}
          {(activeRide || destLoc) && (
            <Marker position={[activeRide ? activeRide.destination.lat : destLoc.lat, activeRide ? activeRide.destination.lng : destLoc.lng]} icon={destIcon}>
              <Popup>{activeRide ? activeRide.destination.name : 'Destino'}</Popup>
            </Marker>
          )}

          {/* Route line */}
          {(activeRide || destLoc) && (
            <Polyline positions={[
              [activeRide ? activeRide.origin.lat : myLoc.lat, activeRide ? activeRide.origin.lng : myLoc.lng],
              [activeRide ? activeRide.destination.lat : destLoc.lat, activeRide ? activeRide.destination.lng : destLoc.lng]
            ]} color="#6366f1" weight={3} dashArray="8" />
          )}

          {/* Driver live location */}
          {driverLoc && (
            <Marker position={[driverLoc.lat, driverLoc.lng]} icon={driverIcon}>
              <Popup>Conductor</Popup>
            </Marker>
          )}
        </MapContainer>
      </div>

      {/* Passenger Tab */}
      {tab === 'passenger' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          {activeRide ? (
            <div data-testid="active-ride">
              <div className="flex justify-between items-start">
                <div>
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${
                    activeRide.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                    activeRide.status === 'accepted' ? 'bg-green-100 text-green-700' :
                    activeRide.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                    'bg-amber-100 text-amber-700'
                  }`} data-testid="ride-status">{STATUSES[activeRide.status]}</span>
                  <h3 className="text-base font-semibold text-gray-900 mt-2">{activeRide.origin.name} → {activeRide.destination.name}</h3>
                  <p className="text-sm text-gray-500">
                    {activeRide.distance_km} km - {activeRide.estimated_fare} Cc
                    {activeRide.eta_display && <span className="ml-2 text-indigo-600 font-medium">| ETA: {activeRide.eta_display}</span>}
                  </p>
                </div>
                {activeRide.driver_name && (
                  <div className="text-right text-sm">
                    <p className="font-medium text-gray-900">{activeRide.driver_name}</p>
                    <p className="text-xs text-gray-400">Conductor</p>
                  </div>
                )}
              </div>

              {activeRide.status === 'completed' && !activeRide.rating_driver && (
                <div className="mt-4 flex items-center gap-3">
                  <span className="text-sm text-gray-600">Califica al conductor:</span>
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map(n => (
                      <button key={n} onClick={() => setRatingValue(n)}
                        className={`w-8 h-8 rounded-full text-sm font-bold ${n <= ratingValue ? 'bg-amber-400 text-white' : 'bg-gray-100 text-gray-400'}`}>{n}</button>
                    ))}
                  </div>
                  <button onClick={() => rateRide(activeRide.id)} data-testid="rate-btn"
                    className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-medium">Calificar</button>
                </div>
              )}

              {!['completed', 'cancelled'].includes(activeRide.status) && (
                <button onClick={cancelRide} data-testid="cancel-ride-btn"
                  className="mt-3 text-sm text-red-500 hover:text-red-700 font-medium">Cancelar Viaje</button>
              )}
            </div>
          ) : (
            <div data-testid="request-ride-form">
              <h3 className="text-base font-semibold text-gray-900">Solicitar Viaje</h3>
              <p className="text-xs text-gray-400 mb-3">Selecciona una ruta o haz clic en el mapa</p>
              
              {/* Predefined Routes */}
              {Object.keys(predefinedRoutes).length > 0 && (
                <div className="mb-4" data-testid="predefined-routes">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-gray-600">Rutas Populares:</span>
                    <div className="flex gap-1 flex-wrap">
                      {Object.entries(predefinedRoutes).map(([code, data]) => (
                        <button key={code} onClick={() => setSelectedCity(selectedCity === code ? '' : code)} data-testid={`city-${code}`}
                          className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${selectedCity === code ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                          {data.city}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {selectedCity && predefinedRoutes[selectedCity] && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 mb-3">
                      {predefinedRoutes[selectedCity].routes.map(route => {
                        const catIcon = route.category === 'aeropuerto' ? 'fa-plane' :
                          route.category === 'comercial' ? 'fa-shopping-bag' :
                          route.category === 'terminal' ? 'fa-bus' :
                          route.category === 'universidad' ? 'fa-graduation-cap' : 'fa-map-pin';
                        return (
                          <button key={route.id} onClick={() => selectRoute(route)} data-testid={`route-${route.id}`}
                            className={`flex items-center gap-1.5 text-left px-2.5 py-2 rounded-lg border text-xs transition-all ${
                              destLoc?.lat === route.lat && destLoc?.lng === route.lng 
                                ? 'border-indigo-400 bg-indigo-50 text-indigo-700' 
                                : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50 text-gray-700'
                            }`}>
                            <i className={`fa ${catIcon} text-indigo-400`}></i>
                            <span className="truncate">{route.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <input value={originName} onChange={e => setOriginName(e.target.value)} placeholder="Nombre origen"
                  data-testid="origin-name-input" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <input value={destName} onChange={e => setDestName(e.target.value)} placeholder="Nombre destino"
                  data-testid="dest-name-input" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>

              {/* ETA & Route Info */}
              {routeEta && destLoc && (
                <div className="mt-3 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-3 border border-indigo-100" data-testid="eta-display">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <i className="fa fa-clock text-indigo-600"></i>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-indigo-800">{routeEta.eta_display}</p>
                        <p className="text-xs text-indigo-500">{routeEta.distance_km} km | {routeEta.speed_type} (~{routeEta.avg_speed_kmh} km/h)</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {routeEta.surge?.active ? (
                        <div data-testid="surge-pricing">
                          <p className="text-xs line-through text-gray-400">{routeEta.estimated_fare} Cc</p>
                          <p className="text-lg font-bold text-red-600">{routeEta.surge_fare} Cc</p>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold">
                            x{routeEta.surge.multiplier} SURGE
                          </span>
                        </div>
                      ) : (
                        <>
                          <p className="text-lg font-bold text-green-700">{routeEta.estimated_fare} Cc</p>
                          <p className="text-xs text-green-600">Tarifa estimada</p>
                        </>
                      )}
                    </div>
                  </div>
                  {routeEta.surge?.active && (
                    <p className="text-xs text-red-500 mt-2 text-center">
                      Alta demanda: {routeEta.surge.demand} solicitudes / {routeEta.surge.supply} conductores disponibles
                    </p>
                  )}
                </div>
              )}

              {destLoc && !routeEta && <p className="text-xs text-green-600 mt-2">Destino seleccionado: {destLoc.lat.toFixed(4)}, {destLoc.lng.toFixed(4)}</p>}
              <button onClick={requestRide} disabled={!destLoc} data-testid="request-ride-btn"
                className="mt-3 w-full bg-indigo-600 text-white py-2.5 rounded-lg hover:bg-indigo-500 disabled:opacity-50 font-medium">
                {routeEta ? `Solicitar Viaje (${routeEta.eta_display} | ${routeEta.estimated_fare} Cc)` : 'Solicitar Viaje'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Driver Tab */}
      {tab === 'driver' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          {!driverProfile ? (
            <div data-testid="register-driver-form">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Registrate como Conductor</h3>
              <div className="space-y-3">
                <select value={regData.vehicle_type} onChange={e => setRegData(p => ({...p, vehicle_type: e.target.value}))}
                  data-testid="vehicle-type" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="auto">Auto</option>
                  <option value="moto">Moto</option>
                  <option value="camioneta">Camioneta</option>
                </select>
                <input value={regData.vehicle_description} onChange={e => setRegData(p => ({...p, vehicle_description: e.target.value}))}
                  placeholder="Descripcion del vehiculo" data-testid="vehicle-desc"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <input value={regData.license_plate} onChange={e => setRegData(p => ({...p, license_plate: e.target.value}))}
                  placeholder="Placa" data-testid="license-plate"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <button onClick={registerDriver} data-testid="register-driver-btn"
                  className="w-full bg-green-600 text-white py-2.5 rounded-lg hover:bg-green-500 font-medium">Registrarse</button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{driverProfile.user_name}</h3>
                  <p className="text-xs text-gray-500">{driverProfile.vehicle_description} - {driverProfile.license_plate}</p>
                  <p className="text-xs text-gray-400">{driverProfile.total_rides} viajes - {driverProfile.rating} estrellas</p>
                </div>
                <button onClick={toggleDriverStatus} data-testid="toggle-driver-status"
                  className={`px-4 py-2 rounded-lg text-sm font-bold ${
                    driverProfile.status === 'available' ? 'bg-green-100 text-green-700' :
                    driverProfile.status === 'busy' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                  {driverProfile.status === 'available' ? 'Disponible' : driverProfile.status === 'busy' ? 'Ocupado' : 'Desconectado'}
                </button>
              </div>

              {activeRide && activeRide.driver_id === user?.id && (
                <div className="bg-indigo-50 rounded-lg p-4 space-y-3" data-testid="driver-active-ride">
                  <p className="text-sm font-semibold text-indigo-800">Viaje activo: {activeRide.origin.name} → {activeRide.destination.name}</p>
                  <p className="text-xs text-indigo-600">Pasajero: {activeRide.passenger_name} | {activeRide.distance_km} km | {activeRide.estimated_fare} Cc</p>
                  <div className="flex gap-2">
                    {activeRide.status === 'accepted' && (
                      <button onClick={startRide} data-testid="start-ride-btn"
                        className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-medium">Pasajero Recogido</button>
                    )}
                    {activeRide.status === 'in_progress' && (
                      <button onClick={completeRide} data-testid="complete-ride-btn"
                        className="flex-1 bg-green-600 text-white py-2 rounded-lg text-sm font-medium">Completar Viaje</button>
                    )}
                    <button onClick={cancelRide} className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium">Cancelar</button>
                  </div>
                </div>
              )}

              {driverProfile.status === 'available' && availableRides.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Solicitudes disponibles</h4>
                  <div className="space-y-2">
                    {availableRides.map(ride => (
                      <div key={ride.id} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between" data-testid={`available-ride-${ride.id}`}>
                        <div>
                          <p className="text-sm font-medium">{ride.origin.name} → {ride.destination.name}</p>
                          <p className="text-xs text-gray-500">
                            {ride.passenger_name} | {ride.distance_km} km | {ride.estimated_fare} Cc
                            {ride.eta_display && ` | ${ride.eta_display}`}
                            {ride.distance_to_pickup !== undefined ? ` | ${ride.distance_to_pickup} km de ti` : ''}
                          </p>
                        </div>
                        <button onClick={() => acceptRide(ride.id)} data-testid={`accept-ride-${ride.id}`}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-bold">Aceptar</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* History */}
      {tab === 'history' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Historial de Viajes</h3>
          </div>
          {myRides.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">Sin viajes aun</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {myRides.map(ride => (
                <div key={ride.id} className="px-5 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{ride.origin.name} → {ride.destination.name}</p>
                    <p className="text-xs text-gray-500">{ride.distance_km} km | {STATUSES[ride.status]} | {new Date(ride.created_at).toLocaleDateString('es-VE')}</p>
                  </div>
                  <span className="text-sm font-bold text-gray-700">{ride.final_fare || ride.estimated_fare} Cc</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransportComponent;
